import { withChartDefaults } from './documentExport';
import { drawAgencyHeader } from './agencyInfo';
import { fillablePdf } from './fillable';

export const chartDefaults = (ctx = {}) => {
  const c = ctx.chart || {};
  const holder = String(c.insurancePolicyHolder || '').trim().split(/\s+/);
  return {
    patientName: ctx.patientName, lastName: c.lastName, firstName: c.firstName,
    middleInitial: (c.middleName || '').slice(0, 1), dateOfBirth: c.dateOfBirth || ctx.dateOfBirth,
    sex: c.gender || c.sex, maritalStatus: c.maritalStatus, ethnicOrigin: c.ethnicity || c.race || c.ethnicOrigin,
    religion: c.religion, address: c.address, city: c.city, state: c.state, zipCode: c.zipCode || c.zip,
    homePhone: c.phone || c.homePhone, cellPhone: c.cellPhone || c.mobilePhone,
    socialSecurityNumber: c.ssn || c.socialSecurityNumber,
    admissionDate: c.admitDate || c.startOfCareDate, medicalRecordNumber: c.medicalRecordNumber || ctx.mrn,
    primaryInsuranceName: c.insurance, primaryPolicyNumber: c.insuranceMemberId,
    primaryGroupNumber: c.insuranceGroupNumber, primaryGroupName: c.insuranceType,
    primaryInsuredFirstName: holder.length > 1 ? holder[0] : '', primaryInsuredLastName: holder.length > 1 ? holder[holder.length - 1] : '',
    primarySubscriberDOB: c.insurancePolicyHolderDOB, primarySubscriberRelation: c.insuranceRelationship,
    secondaryInsuranceName: c.secondaryInsurance, secondaryPolicyNumber: c.secondaryInsuranceMemberId,
    emergencyContact1Name: c.emergencyContactName || c.emergencyContact,
    emergencyContact1Relationship: c.emergencyContactRelationship,
    emergencyContact1CellPhone: c.emergencyContactPhone,
    primaryPhysician: c.primaryPhysician || c.physicianName, primaryDiagnosis: c.primaryDiagnosis,
    secondaryDiagnoses: c.secondaryDiagnoses, allergies: c.allergies, medications: c.currentMedications,
    email: c.email, primaryLanguage: c.primaryLanguage, county: c.county,
    medicareNumber: c.medicareNumber, medicaidNumber: c.medicaidNumber, insurancePayerId: c.insurancePayerId,
    insuranceEffectiveDate: c.insuranceEffectiveDate, primaryInsurancePhone: c.insurancePhone,
    secondaryGroupNumber: c.secondaryInsuranceGroupNumber,
    primaryPhysicianNPI: c.primaryPhysicianNPI, primaryPhysicianPhone: c.primaryPhysicianPhone || c.physicianPhone,
    primaryPhysicianFax: c.primaryPhysicianFax || c.physicianFax, primaryPhysicianAddress: c.primaryPhysicianAddress,
    referringPhysician: c.referringPhysician, referringPhysicianNPI: c.referringPhysicianNPI,
    pharmacyName: c.pharmacyName || c.pharmacy, pharmacyPhone: c.pharmacyPhone, pharmacyAddress: c.pharmacyAddress,
    primaryDiagnosisICD10: c.primaryDiagnosisICD10,
    emergencyContact2Name: c.emergencyContact2Name, emergencyContact2Relationship: c.emergencyContact2Relationship,
    emergencyContact2CellPhone: c.emergencyContact2Phone, emergencyContact1Address: c.emergencyContactAddress,
    emergencyContact1HomePhone: c.emergencyContactAltPhone,
  };
};

const L = 12;
const W = 192;
const FILL = [219, 234, 254];
const BLUE = [30, 64, 175];
const LINE = [96, 125, 200];
const EMPTY = /^(not available.*|n\/a|unknown|none provided)$/i;
const BOLD_LABELS = /^(Cell Phone:|Permission to contact:)/;

const val = (v) => {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.map(val).filter(Boolean).join(', ');
  if (typeof v === 'object') return Object.values(v).map(val).filter(Boolean).join(', ');
  const s = String(v).trim();
  return EMPTY.test(s) ? '' : s;
};

const medLine = (m) => (m && typeof m === 'object'
  ? [m.name || m.medication || m.medicationName || m.drugName, m.dose || m.dosage || m.dosageStrength || m.strength, m.route, m.frequency || m.timeFrequency].map(val).filter(Boolean).join(' ')
  : val(m));

export const medsText = (meds) => {
  if (typeof meds === 'string') {
    const t = meds.trim();
    if (t.startsWith('[')) { try { return medsText(JSON.parse(t)); } catch { /* plain text */ } }
    return t.split(/\s*[;\n]\s*/).map(val).filter(Boolean).join('\n');
  }
  return Array.isArray(meds) ? meds.map(medLine).filter(Boolean).join('\n') : val(meds);
};

const options = (choices, selected) => {
  const sel = (selected || '').toLowerCase();
  return choices.map(([code, full]) => {
    const on = sel && (sel === code.toLowerCase() || sel === full.toLowerCase() || sel.startsWith(full.toLowerCase()));
    return `(${on ? 'X' : ' '})${code}`;
  }).join(' ');
};

const splitName = (full = '') => {
  const parts = val(full).split(/\s+/).filter(Boolean);
  if (parts.length < 2) return { first: parts[0] || '', last: '', mi: '' };
  return { first: parts[0], last: parts[parts.length - 1], mi: parts.length > 2 ? parts[1][0] : '' };
};

const usDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(val(v));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : val(v);
};

const ageFrom = (dob) => {
  const d = new Date(dob);
  if (!dob || Number.isNaN(d.getTime())) return '';
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  if (now < new Date(now.getFullYear(), d.getMonth(), d.getDate())) a -= 1;
  return String(a);
};

const makeCtx = (doc) => {
  let y = 18;
  const ends = [];
  const ensure = (h) => { if (y + h > 266) { ends.push(y); doc.addPage(); y = 18; } };
  const section = (title, extra = '') => {
    ensure(14);
    doc.setDrawColor(...BLUE); doc.setFillColor(...BLUE); doc.setLineWidth(0.3);
    doc.rect(L, y, W, 5, 'FD');
    doc.setFont('times', 'bold'); doc.setFontSize(9.5); doc.setTextColor(255);
    doc.text(title, L + 1.5, y + 3.7);
    if (extra) doc.text(extra, L + 60, y + 3.7);
    doc.setTextColor(0);
    y += 5;
  };
  // full-width option lines split into equal columns, no fill
  const line = (cols) => {
    const w = W / cols.length;
    const h = Math.max(...cols.map(t => t.split('\n').length)) * 4.2 + 2;
    ensure(h);
    cols.forEach((t, i) => {
      doc.setDrawColor(...LINE); doc.rect(L + i * w, y, w, h);
      doc.setFont('times', 'normal'); doc.setFontSize(8.5); doc.setTextColor(0);
      doc.text(t.split('\n'), L + i * w + 1.2, y + 3.8);
    });
    y += h;
  };
  const text = (t, bold, h, prefix = '', size = 8.8) => {
    ensure(h);
    doc.setDrawColor(...LINE); doc.rect(L, y, W, h);
    const top = y + size * 0.45 + 0.5;
    doc.setFontSize(size); doc.setTextColor(0);
    let first = '';
    if (prefix) {
      doc.setFont('times', 'bold'); doc.text(prefix, L + 1.5, top);
      first = ' '.repeat(Math.ceil(doc.getTextWidth(`${prefix} `) / doc.getTextWidth(' ')));
    }
    doc.setFont('times', bold ? 'bold' : 'normal');
    doc.text(doc.splitTextToSize(first + t, W - 4), L + 1.5, top);
    y += h;
  };
  // cells: [label, value, widthFraction(of 12)]
  const row = (cells, opt) => {
    const unit = W / 12;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.8);
    const heights = cells.map(([, v, span]) => doc.splitTextToSize(v || '', span * unit - 3).length);
    const h = typeof opt === 'number' ? opt : Math.max(opt?.minH || 9.4, 5.6 + Math.max(...heights) * 3.2);
    ensure(h);
    let x = L;
    cells.forEach(([label, v, span], i) => {
      const w = span * unit;
      doc.setFillColor(...FILL);
      if (typeof opt !== 'number' && !(opt === 'nofill-2' && i === 1)) doc.rect(x + 0.4, y + 4.6, w - 0.8, h - 5, 'F');
      doc.setDrawColor(...LINE); doc.rect(x, y, w, h);
      doc.setFont('times', BOLD_LABELS.test(label) ? 'bold' : 'normal'); doc.setFontSize(8.5); doc.setTextColor(0);
      doc.text(label, x + 1.2, y + 3.6);
      if (typeof opt !== 'number' && doc.addFill) doc.addFill({ type: 'text', x: x + 0.6, y: y + 4.7, w: w - 1.2, h: h - 5.2, value: v || '', multiline: h > 11, fontSize: 7.8 });
      else if (v) {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.8); doc.setTextColor(20, 20, 90);
        doc.text(doc.splitTextToSize(v, w - 3), x + 1.5, y + 7.6);
      }
      x += w;
    });
    y += h;
  };
  return {
    section, row, ensure, line, text,
    blank: (h) => { doc.setDrawColor(...LINE); doc.rect(L, y, W, h); y += h; },
    newPage: () => { ends.push(y); doc.addPage(); y = 18; },
    pageEnd: (i) => (ends[i - 1] !== undefined ? ends[i - 1] : y),
    getY: () => y,
  };
};

export const buildFacesheetPdf = (rawData = {}, patientContext = {}) => {
  const data = withChartDefaults(rawData, chartDefaults(patientContext));
  const doc = fillablePdf({ unit: 'mm', format: 'letter' });
  const d = (k) => val(data[k]);
  doc.patientInfo = { name: d('patientName') || patientContext.patientName, dob: d('dateOfBirth') || patientContext.dateOfBirth, mrn: patientContext.mrn || d('medicalRecordNumber'), dx: d('primaryDiagnosis') || patientContext.primaryDiagnosis, admission: d('startOfCareDate') || d('admissionDate') || patientContext.startOfCareDate || patientContext.admissionDate };
  const name = splitName(d('patientName') || patientContext.patientName);
  const rawDob = d('dateOfBirth') || val(patientContext.dateOfBirth);
  const dob = usDate(rawDob);
  const fromChart = !!patientContext.chart;
  const ins = data.insuranceInfo && typeof data.insuranceInfo === 'object' && !Array.isArray(data.insuranceInfo) ? data.insuranceInfo : {};
  const ec = splitName(d('emergencyContact'));

  drawAgencyHeader(doc, L, 1.5, { size: 13 });
  doc.setFont('times', 'bold'); doc.setFontSize(15); doc.setTextColor(...BLUE);
  doc.text('FACE SHEET', 128, 9, { align: 'center' });
  doc.setFont('times', 'normal'); doc.setFontSize(8.5); doc.setTextColor(0);
  doc.text(`Admission Date: ${usDate(d('admissionDate'))}`, L + W, 6, { align: 'right' });
  doc.text(`MR#: ${d('medicalRecordNumber') || val(patientContext.mrn)}`, L + W, 11, { align: 'right' });

  const c = makeCtx(doc);
  c.section('1. Patient Information:');
  c.row([['Last Name:', d('lastName') || name.last, 5], ['First Name:', d('firstName') || name.first, 5], ['M. Initial:', d('middleInitial') || (fromChart ? '' : name.mi), 2]]);
  c.row([
    ['Sex:', options([['M', 'Male'], ['F', 'Female']], d('sex') || d('gender')), 1.5],
    ['DOB:', dob, 1.5],
    ['Age:', ageFrom(rawDob) || d('age'), 0.8],
    ['Marital Status:', options([['S', 'Single'], ['W', 'Widowed'], ['M', 'Married'], ['D', 'Divorced'], ['Separated', 'Separated']], d('maritalStatus')), 2.9],
    ['Ethnic Origin:', options([['Caucasian', 'Caucasian'], ['African-American', 'African-American'], ['American Indian', 'American Indian'], ['Hispanic', 'Hispanic'], ['Asian', 'Asian'], ['Other', 'Other']], d('ethnicOrigin')), 3.8],
    ['Religion:', d('religion'), 1.5],
  ]);
  c.row([['Address:', d('address'), 5], ['Apt#:', d('apt'), 1.5], ['City:', d('city'), 3], ['State/Zip:', [d('state'), d('zipCode')].filter(Boolean).join(' '), 2.5]]);
  c.row([['Home Phone:', d('homePhone') || d('phone'), 3.3], ['Cell Phone:', d('cellPhone'), 3.3], ['Social Security #:', d('socialSecurityNumber') || d('ssn'), 2.9], ["Driver's License and State:", [d('driversLicense'), d('driversLicenseState')].filter(Boolean).join(' '), 2.5]]);
  c.row([['Email:', d('email'), 4.6], ['Primary Language:', d('primaryLanguage'), 3.2], ['County:', d('county'), 4.2]]);
  c.row([['Vehicle Make/Model:', d('vehicleMakeModel'), 4.6], ['Year:', d('vehicleYear'), 1.3], ['Color:', d('vehicleColor'), 1.3], ['License Plate#:', d('licensePlate'), 4.8]]);
  c.row([['Employer Name:', d('employerName'), 3.3], ['Occupation:', d('occupation'), 2.7], ['Length of Employment:', d('lengthOfEmployment'), 3.4], ['Employer Phone:', d('employerPhone'), 2.6]]);
  c.row([['Employer Address:', d('employerAddress'), 4.6], ['Suite#:', d('employerSuite'), 1.3], ['City:', d('employerCity'), 2.8], ['State/Zip:', [d('employerState'), d('employerZip')].filter(Boolean).join(' '), 3.3]]);

  c.section('2. Guarantor/Legal Guardian of Minor:');
  c.row([['Last Name:', d('guarantorLastName'), 3.8], ['First:', d('guarantorFirstName'), 4], ['Sex:', options([['M', 'Male'], ['F', 'Female']], d('guarantorSex')), 1], ['DOB:', usDate(d('guarantorDOB')), 1.4], ['Relation:', d('guarantorRelation'), 1.8]]);
  c.row([['Cell Phone:', d('guarantorCellPhone'), 3.8], ['Social Security#:', d('guarantorSSN'), 4], ['M. Initial:', d('guarantorMiddleInitial'), 1], ['Occupation:', d('guarantorOccupation'), 3.2]]);
  c.row([['Address:', d('guarantorAddress'), 5.2], ['Apt #:', d('guarantorApt'), 1.3], ['City:', d('guarantorCity'), 2.3], ['State/Zip:', [d('guarantorState'), d('guarantorZip')].filter(Boolean).join(' '), 3.2]]);
  c.row([['Employer Name:', d('guarantorEmployerName'), 5.8], ['Length of Employment:', d('guarantorLengthOfEmployment'), 3.1], ['Employer Phone:', d('guarantorEmployerPhone'), 3.1]]);

  c.section('3. Primary Insurance Information:');
  c.row([['Name of Insurance:', d('primaryInsuranceName') || val(ins.provider) || (typeof data.insuranceInfo === 'string' ? d('insuranceInfo') : ''), 7.3], ['Insurance Phone:', d('primaryInsurancePhone'), 4.7]]);
  c.row([['Policy/Hic#:', d('primaryPolicyNumber') || val(ins.memberID || ins.memberId || ins.policyNumber), 4.6], ['Social Security #:', d('primarySubscriberSSN'), 3.2], ['Group Name:', d('primaryGroupName') || val(ins.planType), 2.3], ['Group#:', d('primaryGroupNumber') || val(ins.groupID || ins.groupId || ins.groupNumber), 1.9]]);
  c.row([['Medicare #:', d('medicareNumber'), 3.3], ['Medicaid #:', d('medicaidNumber'), 3.3], ['Payer ID:', d('insurancePayerId'), 2.7], ['Effective Date:', usDate(d('insuranceEffectiveDate')), 2.7]]);
  c.row([["Insured's Last Name:", d('primaryInsuredLastName'), 3.3], ['First:', d('primaryInsuredFirstName'), 2.9], ['Middle Initial:', d('primaryInsuredMiddleInitial'), 1.5], ['Sex:', options([['M', 'Male'], ['F', 'Female']], d('primaryInsuredSex')), 1.1], ['Relation:', d('primarySubscriberRelation'), 1.6], ['DOB:', usDate(d('primarySubscriberDOB')), 1.6]]);
  c.row([['Employer Name:', d('primarySubscriberEmployer'), 3.3], ['Occupation:', d('primaryInsuredOccupation'), 2.7], ['Length of Employment:', d('primaryInsuredLengthOfEmployment'), 3.4], ['Employer Phone:', d('primaryInsuredEmployerPhone'), 2.6]]);
  c.row([['Employer Address:', d('primaryInsuredEmployerAddress'), 4.6], ['Suite#:', d('primaryInsuredEmployerSuite'), 1.3], ['City:', d('primaryInsuredEmployerCity'), 2.8], ['State/Zip:', d('primaryInsuredEmployerStateZip'), 3.3]]);

  const hasSecondary = !!d('secondaryInsuranceName');
  c.section('4. Secondary Insurance:', `${options([['None-Go to Section 5', 'none']], hasSecondary ? '' : 'none')}     ${options([['Yes - Complete Section 4', 'yes']], hasSecondary ? 'yes' : '')}`);
  c.row([['Name of Insurance:', d('secondaryInsuranceName'), 7.3], ['Insurance Phone:', d('secondaryInsurancePhone'), 4.7]]);
  c.row([['Policy/Hic#:', d('secondaryPolicyNumber'), 4.6], ['Social Security #:', d('secondarySubscriberSSN'), 3.2], ['Group Name:', d('secondaryGroupName'), 2.3], ['Group#:', d('secondaryGroupNumber'), 1.9]]);
  const sec = splitName(d('secondarySubscriberName'));
  c.row([["Insured's Last Name:", d('secondaryInsuredLastName') || sec.last, 3.3], ['First:', d('secondaryInsuredFirstName') || sec.first, 2.9], ['Middle Initial:', d('secondaryInsuredMiddleInitial') || sec.mi, 1.5], ['Sex:', options([['M', 'Male'], ['F', 'Female']], d('secondaryInsuredSex')), 1.1], ['Relation:', d('secondarySubscriberRelation'), 1.6], ['DOB:', usDate(d('secondarySubscriberDOB')), 1.6]]);
  c.row([['Employer Name:', d('secondarySubscriberEmployer'), 3.3], ['Occupation:', d('secondaryInsuredOccupation'), 2.7], ['Length of Employment:', d('secondaryInsuredLengthOfEmployment'), 3.4], ['Employer Phone:', d('secondaryInsuredEmployerPhone'), 2.6]]);
  c.row([['Employer Address:', d('secondaryInsuredEmployerAddress'), 4.6], ['Suite#:', d('secondaryInsuredEmployerSuite'), 1.3], ['City:', d('secondaryInsuredEmployerCity'), 2.8], ['State/Zip:', d('secondaryInsuredEmployerStateZip'), 3.3]]);

  c.newPage();
  c.section('5. Physician & Pharmacy:');
  c.row([['Primary Physician:', d('primaryPhysician'), 4.6], ['NPI:', d('primaryPhysicianNPI'), 2.4], ['Phone:', d('primaryPhysicianPhone'), 2.5], ['Fax:', d('primaryPhysicianFax'), 2.5]]);
  c.row([['Physician Address:', d('primaryPhysicianAddress'), 7.2], ['Referring Physician:', [d('referringPhysician'), d('referringPhysicianNPI') && `NPI ${d('referringPhysicianNPI')}`].filter(Boolean).join(' - '), 4.8]]);
  c.row([['Pharmacy Name:', d('pharmacyName'), 4.6], ['Pharmacy Phone:', d('pharmacyPhone'), 2.4], ['Pharmacy Address:', d('pharmacyAddress'), 5]]);

  c.section('6. Emergency Contact:');
  [1, 2].forEach(n => {
    const nm = n === 1 ? (d('emergencyContact1Name') || [ec.first, ec.mi, ec.last].filter(Boolean).join(' ')) : d('emergencyContact2Name');
    c.row([[`Emergency Contact #${n}:`, nm, 8.25], ['Relationship:', d(`emergencyContact${n}Relationship`), 3.75]]);
    c.row([['Address:', d(`emergencyContact${n}Address`), 7.2], ['Apt#:', d(`emergencyContact${n}Apt`), 1.05], ['City:', d(`emergencyContact${n}City`), 2.08], ['State/Zip:', d(`emergencyContact${n}StateZip`), 1.67]]);
    c.row([['Home Phone:', d(`emergencyContact${n}HomePhone`), 4.47], ['Cell Phone:', d(`emergencyContact${n}CellPhone`) || (n === 1 ? d('emergencyContactPhone') : ''), 3.78], ['Work Phone:', d(`emergencyContact${n}WorkPhone`), 3.75]]);
  });

  c.section('7. Diagnoses, Allergies & Medications:');
  c.row([['Primary Diagnosis:', d('primaryDiagnosis'), 9], ['ICD-10:', d('primaryDiagnosisICD10'), 3]]);
  c.row([['Secondary Diagnoses:', d('secondaryDiagnoses'), 7.5], ['Allergies:', d('allergies'), 4.5]], { minH: 12 });
  const meds = medsText(data.medications).split('\n').filter(Boolean);
  if (meds.length > 3) {
    const half = Math.ceil(meds.length / 2);
    c.row([['Current Medications:', meds.slice(0, half).join('\n'), 6], ['', meds.slice(half).join('\n'), 6]], { minH: 13 });
  } else c.row([['Current Medications:', meds.join('\n'), 12]], { minH: 13 });

  c.section('8. Previous Hospitalizations:');
  c.row([[`Last 12 months: ${options([['Yes', 'Yes'], ['No', 'No']], d('hospitalization12Months'))}`, '', 6.35], [`Last 6 months: ${options([['Yes', 'Yes'], ['No', 'No']], d('hospitalization6Months'))}`, '', 5.65]], 5.2);
  [['Where:', 'Where'], ['When:', 'When'], ['Why:', 'Why'], ['How long:', 'HowLong']].forEach(([label, k]) => {
    c.row([[label, d(`hospitalization12${k}`), 6.35], [label, d(`hospitalization6${k}`), 5.65]]);
  });

  c.section('9. How did you hear of IVORY Home Health Care?');
  const heard = d('referralSource');
  c.line([`${options([['Mental Health Professional', 'Mental Health Professional'], ['Legal/Judicial', 'Legal/Judicial'], ['Psychiatrist', 'Psychiatrist'], ['Clergy/Church', 'Clergy/Church'], ['Family/Friend', 'Family/Friend'], ['Internet', 'Internet']], heard)}\n${options([['Insurance Company', 'Insurance Company'], ['Previous IVORY Patient', 'Previous Patient'], ['Advertisement', 'Advertisement'], ['Organization', 'Organization'], ['Other', 'Other']], heard)}`]);

  const purpose = 'To identify persons supporting and using services; notification of admission, discharge, and aftercare plans.';
  const notice = 'All requested information must be completed for insurance claims to be correctly processed. Exclusion of insurance policy information may result in an insurance denial in which you will be totally responsible for your bill. The person who signs consent is the Guarantor/responsible party for this bill.';
  // shrink the closing notice rather than spill onto a near-empty extra page
  if (266 - c.getY() >= 19.5) {
    c.text(purpose, false, 7.5, 'Purpose of disclosure:');
    c.text(notice, true, Math.min(24, Math.max(12, 266 - c.getY())));
  } else c.text(`${purpose} ${notice}`, false, Math.max(10.5, 266 - c.getY()), 'Purpose of disclosure:', 6.5);

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setDrawColor(...BLUE); doc.setLineWidth(0.8);
    doc.rect(L - 1, 16.5, W + 2, c.pageEnd(i) - 15.5);
    doc.setLineWidth(0.3);
    doc.setFont('times', 'bold'); doc.setFontSize(11); doc.setTextColor(...BLUE);
    doc.text('FACE SHEET', L + 4, 273.8);
    doc.setFont('times', 'normal'); doc.setFontSize(8); doc.setTextColor(0);
    doc.text(`Page ${i} of ${pages}`, 108, 273.5, { align: 'center' });
    doc.setFontSize(10);
    doc.text('PATIENT LABEL', L + W - 30, 271.5, { align: 'center' });
    doc.setFontSize(8);
    doc.text('IVORY Home Health Care', L + 4, 269);
  }
  return doc;
};
